import React from 'react';
import Avatar, { AvatarItem } from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import DynamicTable, { type DynamicTableProps } from '@atlaskit/dynamic-table';

import Shortcut from '@atlaskit/icon/glyph/shortcut';
import { Box, Flex } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';

import type { ResourceState, SearchTeamsResponse } from '../../../../common/types';
import { messages } from './messages';

interface TeamsTableProps {
	teams: ResourceState<SearchTeamsResponse>;
}

export const TeamsTable = ({ teams }: TeamsTableProps) => {
	const { formatMessage } = useIntl();

	if (!teams.data) {
		return null;
	}

	const rows: DynamicTableProps['rows'] = teams.data.entities.map((item, index) => ({
		key: `row-${index}`,
		cells: [
			{
				key: `cell-${index}-name`,
				content: (
					<AvatarItem
						avatar={<Avatar name={item.displayName} src={item.smallAvatarImageUrl} />}
						primaryText={item.displayName}
						secondaryText={formatMessage(messages.memberCountNonFinal, {
							count: item.memberCount,
						})}
					/>
				),
			},
			{
				key: `cell-${index}-name`,
				content: (
					<Flex justifyContent="end">
						<Button
							appearance="subtle-link"
							href={`/jira/people/team/${item.id}`}
							target="_blank"
							rel="noopener noreferrer"
							iconAfter={<Shortcut label="" />}
						>
							{formatMessage(messages.viewNonFinal)}
						</Button>
					</Flex>
				),
			},
		],
	}));

	const head = {
		cells: [
			{
				key: 'name',
				content: <Box paddingInlineStart="space.100">{formatMessage(messages.teamNonFinal)}</Box>,
				isSortable: true,
			},
			{
				key: 'type',
				content: null,
			},
		],
	};

	return (
		<DynamicTable
			isLoading={teams.loading}
			head={head}
			rows={rows}
			rowsPerPage={5}
			defaultPage={1}
		/>
	);
};
